import { useEffect, useState } from "react";
import { Toaster } from "react-hot-toast";
import { Provider } from "react-redux";
import { BrowserRouter, useNavigate } from "react-router-dom";

import { PermissionProvider } from "../../auth/permissions/PermissionProvider";
import { store } from "../../redux/store";
import { setReduxStore } from "../../services/axios";
import { ThemeProvider } from "../../theme/ThemeProvider";
import { Button } from "../Button";
import { Modal } from "../Modal";

import { QueryProvider } from "./queryProvider"; // adjust path as needed

import { AcademicYearProvider } from "@/features/academics/academicYearContext/AcademicYearContext";
import { ProfileNavigationBoundary } from "@/features/academics/students/components/ProfileNavigationBoundary";
import { BrandKitBootstrap } from "@/features/branding/BrandKitBootstrap";
import { HostTenantBootstrap } from "@/features/hostTenant/HostTenantBootstrap";
import { TenantBrandingBootstrap } from "@/features/tenantBranding/TenantBrandingBootstrap";
import { TenantDocumentChrome } from "@/features/tenantBranding/TenantDocumentChrome";
import {
  SESSION_EXPIRED_EVENT,
  type SessionExpiredDetail,
} from "@/services/apiErrorEvents";

setReduxStore(store);

interface ProvidersProps {
  children: React.ReactNode;
}

export function Providers({ children }: ProvidersProps) {
  return (
    <Provider store={store}>
      <ProfileNavigationBoundary />
      <BrowserRouter>
        <HostTenantBootstrap />
        <QueryProvider>
          {/* wrap React Query here */}
          <ThemeProvider>
            <PermissionProvider>
              <AcademicYearProvider>
                <TenantBrandingBootstrap />
                <BrandKitBootstrap />
                <TenantDocumentChrome />
                {children}
              </AcademicYearProvider>
              <SessionExpiredDialog />
              <Toaster
                position="top-center"
                containerStyle={{
                  top: 72,
                  left: 16,
                  right: 16,
                }}
                toastOptions={{
                  duration: 4000,
                  className: "sms-snackbar",
                  success: {
                    className: "sms-snackbar sms-snackbar--success",
                  },
                  error: {
                    className: "sms-snackbar sms-snackbar--error",
                  },
                  loading: {
                    className: "sms-snackbar sms-snackbar--loading",
                  },
                }}
              />
            </PermissionProvider>
          </ThemeProvider>
        </QueryProvider>
      </BrowserRouter>
    </Provider>
  );
}

function SessionExpiredDialog() {
  const [sessionDialog, setSessionDialog] = useState<SessionExpiredDetail | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<SessionExpiredDetail>).detail;
      if (!detail) return;
      setSessionDialog(detail);
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handler);
  }, []);

  const handleSessionConfirm = () => {
    setSessionDialog(null);
    navigate("/login", { replace: true });
  };

  return (
    <Modal
      isOpen={Boolean(sessionDialog)}
      onClose={() => {}}
      title={sessionDialog?.title ?? "Session expired"}
      showCloseButton={false}
      size="sm"
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {sessionDialog?.message ?? "Please sign in again to continue."}
        </p>
        <div className="flex justify-end">
          <Button onClick={handleSessionConfirm}>Go to login</Button>
        </div>
      </div>
    </Modal>
  );
}
